/**
 *  @class
 *  @function FacetsToggle
 */
class FacetsToggle extends HTMLElement {
  constructor() {
    super();
    this.drawer = document.getElementById('Facet-Drawer');
  }
  connectedCallback() {
    this.addEventListener('click', () => {
      document.body.classList.toggle('open-cc');
      this.drawer.classList.toggle('active');
      this.drawer.removeAttribute('inert');
    });
  }
}
customElements.define('facet-toggle', FacetsToggle);

/**
 *  @class
 *  @function SidebarToggle
 */
class SidebarToggle extends HTMLElement {
  constructor() {
    super();
    this.drawer = document.getElementById('Sidebar-Container');
  }
  connectedCallback() {
    const span = this.querySelector('span');
    const hideFilters = span.textContent;

    this.addEventListener('click', (e) => {
      e.preventDefault();
      this.drawer.classList.toggle('facets--sidebar-open');

      span.textContent = this.drawer.classList.contains('facets--sidebar-open') ? hideFilters : theme.strings.showFilters;
    });
  }
}
customElements.define('sidebar-toggle', SidebarToggle);
/**
 *  @class
 *  @function FacetFiltersForm
 */
class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.form = this.querySelector('form');

    // Create debounced submit that captures form data immediately
    this.debouncedOnSubmit = debounce((formData, targetIndex, targetFormId) => {
      this.onSubmitHandler(formData, targetIndex, targetFormId);
    }, 500);

    this.querySelectorAll('.thb-filter-content--button').forEach((more_button) => {
      more_button.addEventListener('click', this.onMore);
    });
    
    // Capture form data and target index immediately on input, before debounce
    this.form.addEventListener('input', (event) => {
      // Small delay to ensure checkbox state is updated
      setTimeout(() => {
        const formData = new FormData(this.form);
        const targetRow = event.target.closest('collapsible-row');
        const targetIndex = targetRow ? targetRow.dataset.index : null;
        const targetFormId = this.form.id;
        this.debouncedOnSubmit(formData, targetIndex, targetFormId);
      }, 0);
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const container = document.querySelectorAll('.thb-filter-count, .mobile-filter-count');
    const productsGrid = document.getElementById('ProductGridContainer')?.querySelector('.products');
    if (productsGrid) {
      productsGrid.classList.add('loading');
    }

    for (var item of container) {
      item.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event);
      }
    });
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, {
          html,
          url
        }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);

      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);

  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;

    ScrollTrigger.batch('.animations-true .products .product-card--image-primary', {
      start: "top 90%",
      onEnter: (elements) => {
        gsap.to(elements, { scale: 1, opacity: 1, stagger: 0.15, ease: window.theme.settings.animation_easing });
      }
    });
    document.getElementById('ProductGridContainer').querySelectorAll('.lazyload').forEach((image) => {
      lazySizes.loader.unveil(image);
    });
  }

  static renderProductCount(html) {
    const countHtml = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    const container = document.getElementsByClassName('thb-filter-count');

    if (countHtml) {
      for (var item of container) {
        item.innerHTML = countHtml.innerHTML;
        item.classList.remove('loading');
      }
    }

  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    // Capture all open details states BEFORE any updates (for both desktop and mobile)
    const openDetailsMap = new Map();
    document.querySelectorAll('#FacetFiltersForm collapsible-row, #FacetFiltersFormMobile collapsible-row').forEach(row => {
      const form = row.closest('form');
      const formId = form ? form.id : 'unknown';
      const details = row.querySelector('details');
      if (details && details.hasAttribute('open')) {
        openDetailsMap.set(`${formId}-${row.dataset.index}`, true);
      }
    });

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm collapsible-row, #FacetFiltersFormMobile collapsible-row');
    
    // Get target index from event object
    const targetIndex = event?.targetIndex || null;
    const targetFormId = event?.targetFormId || null;
    
    // Match by both index AND form ID to preserve only the specific filter that was clicked
    const matchesTarget = (element) => {
      if (!targetIndex) return false;
      const elementForm = element.closest('form');
      const elementFormId = elementForm ? elementForm.id : null;
      // Match if same index AND same form (or if form ID wasn't captured, just match index)
      if (targetFormId) {
        return element.dataset.index === targetIndex && elementFormId === targetFormId;
      }
      return element.dataset.index === targetIndex;
    };
    
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesTarget(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesTarget);

    facetsToRender.forEach((element) => {
      // Find the form this element belongs to in the new HTML
      const newForm = element.closest('form');
      const newFormId = newForm ? newForm.id : 'unknown';
      
      // Find corresponding row in the current document (same form, same index)
      const currentForm = document.getElementById(newFormId);
      if (!currentForm) return;
      
      const currentRow = currentForm.querySelector(`collapsible-row[data-index="${element.dataset.index}"]`);
      if (!currentRow) return;
      
      // Update the content
      currentRow.innerHTML = element.innerHTML;
      
      // Restore the open state if it was open
      if (openDetailsMap.has(`${newFormId}-${element.dataset.index}`)) {
        const newDetails = currentRow.querySelector('details');
        if (newDetails) {
          newDetails.setAttribute('open', '');
        }
      }
    });

    // Update horizontal facets dropdowns (update filter counts and disabled states)
    FacetFiltersForm.renderHorizontalFacets(parsedHTML, event);

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    // Find the actual DOM element for renderCounts
    if (countsToRender && targetIndex) {
      let targetRow = null;
      if (targetFormId) {
        const form = document.getElementById(targetFormId);
        if (form) {
          targetRow = form.querySelector(`collapsible-row[data-index="${targetIndex}"]`);
        }
      }
      if (!targetRow) {
        targetRow = document.querySelector(`collapsible-row[data-index="${targetIndex}"]`);
      }
      if (targetRow) {
        FacetFiltersForm.renderCounts(countsToRender, targetRow);
      }
    }
  }

  static renderHorizontalFacets(html, event) {
    // Get the horizontal facets container from the new HTML
    const newHorizontalFilters = html.querySelector('.facets-horizontal-filters');
    const currentHorizontalFilters = document.querySelector('.facets-horizontal-filters');
    
    if (!newHorizontalFilters || !currentHorizontalFilters) return;
    
    // Get all dropdowns from both old and new HTML
    const newDropdowns = newHorizontalFilters.querySelectorAll('.facet-dropdown');
    const currentDropdowns = currentHorizontalFilters.querySelectorAll('.facet-dropdown');
    
    newDropdowns.forEach((newDropdown, index) => {
      const currentDropdown = currentDropdowns[index];
      if (!currentDropdown) return;
      
      const filterType = currentDropdown.dataset.filterType;
      
      // Check if this dropdown is currently open (user is interacting with it)
      const isOpen = currentDropdown.classList.contains('is-open');
      
      // Get the dropdown content (the list of filter options)
      const newContent = newDropdown.querySelector('.facet-dropdown-content');
      const currentContent = currentDropdown.querySelector('.facet-dropdown-content');
      
      if (newContent && currentContent) {
        // For price range filters, only update the input values, not the entire container
        // This preserves the noUiSlider instance
        if (filterType === 'price_range') {
          const newMinInput = newContent.querySelector('.facet-price-input-min');
          const newMaxInput = newContent.querySelector('.facet-price-input-max');
          const currentMinInput = currentContent.querySelector('.facet-price-input-min');
          const currentMaxInput = currentContent.querySelector('.facet-price-input-max');
          const currentSlider = currentContent.querySelector('.facet-price-slider');
          
          // Update input values if slider exists and values have changed from server
          if (currentSlider && currentSlider.noUiSlider && newMinInput && newMaxInput) {
            const newMin = parseFloat(newMinInput.value) || 0;
            const newMax = parseFloat(newMaxInput.value) || currentSlider._rangeMax || 10000;
            
            // Only update slider if we're not currently dragging it
            // and the values are different from current
            const currentValues = currentSlider.noUiSlider.get();
            if (Math.abs(parseFloat(currentValues[0]) - newMin) > 1 ||
                Math.abs(parseFloat(currentValues[1]) - newMax) > 1) {
              currentSlider.noUiSlider.set([newMin, newMax]);
            }
          }
        } else {
          // For list filters, update the content (includes new counts and disabled states)
          currentContent.innerHTML = newContent.innerHTML;
        }
        
        // Restore open state if it was open
        if (isOpen) {
          currentContent.hidden = false;
        }
      }
      
      // Update the has-active-filters class
      if (newDropdown.classList.contains('has-active-filters')) {
        currentDropdown.classList.add('has-active-filters');
      } else {
        currentDropdown.classList.remove('has-active-filters');
      }
      
      // Check if ALL options in this dropdown are disabled (count == 0)
      // If so, disable the entire dropdown button
      if (filterType === 'list') {
        const allOptions = currentDropdown.querySelectorAll('.facet-checkbox-label');
        const disabledOptions = currentDropdown.querySelectorAll('.facet-checkbox-label.facet-checkbox--disabled');
        
        if (allOptions.length > 0 && allOptions.length === disabledOptions.length) {
          currentDropdown.classList.add('all-options-disabled');
        } else {
          currentDropdown.classList.remove('all-options-disabled');
        }
      }
    });
    
    // Reinitialize price sliders if they don't exist yet (for new dropdowns)
    if (typeof window.horizontalFacets !== 'undefined' && window.horizontalFacets.initPriceSliders) {
      window.horizontalFacets.initPriceSliders();
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const currentElement = document.querySelector(selector);
      if (!activeFacetsElement || !currentElement) return;
      currentElement.innerHTML = activeFacetsElement.innerHTML;
    });
    
    // Update horizontal active facets without replacing entire container (keeps dropdown open)
    const newActiveFacetsHorizontal = html.querySelector('.active-facets-horizontal');
    const currentActiveFacetsHorizontal = document.querySelector('.active-facets-horizontal');
    const facetsBar = document.querySelector('.facets-horizontal-bar');
    
    if (newActiveFacetsHorizontal) {
      if (currentActiveFacetsHorizontal) {
        currentActiveFacetsHorizontal.innerHTML = newActiveFacetsHorizontal.innerHTML;
      } else if (facetsBar) {
        // Create new active facets container if it doesn't exist
        const newContainer = document.createElement('div');
        newContainer.className = 'active-facets-horizontal';
        newContainer.innerHTML = newActiveFacetsHorizontal.innerHTML;
        facetsBar.parentNode.insertBefore(newContainer, facetsBar.nextSibling);
      }
    } else if (currentActiveFacetsHorizontal) {
      // Remove active facets if none in new HTML
      currentActiveFacetsHorizontal.remove();
    }
    
    // Update product count in horizontal bar
    const newProductCount = html.querySelector('.facets-product-count');
    const currentProductCount = document.querySelector('.facets-product-count');
    if (newProductCount && currentProductCount) {
      currentProductCount.innerHTML = newProductCount.innerHTML;
    }
    
    // Update has-active-filters class on dropdown buttons
    FacetFiltersForm.updateDropdownActiveStates();

    FacetFiltersForm.toggleActiveFacets(false);
  }
  
  static updateDropdownActiveStates() {
    document.querySelectorAll('.facet-dropdown').forEach(dropdown => {
      const filterType = dropdown.dataset.filterType;
      let hasActiveFilters = false;
      
      if (filterType === 'list') {
        hasActiveFilters = dropdown.querySelectorAll('.facet-checkbox-input:checked').length > 0;
      } else if (filterType === 'price_range') {
        const priceInputs = dropdown.querySelectorAll('.facet-price-input');
        priceInputs.forEach(input => {
          if (input.value && input.value.trim() !== '') {
            hasActiveFilters = true;
          }
        });
      }
      
      if (hasActiveFilters) {
        dropdown.classList.add('has-active-filters');
      } else {
        dropdown.classList.remove('has-active-filters');
      }
    });
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-filter-count'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
      document.querySelector(selector).classList.remove('loading');
    });

  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({
      searchParams
    }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }];
  }

  onSubmitHandler(formData, targetIndex, targetFormId) {
    const searchParams = new URLSearchParams(formData);
    if (searchParams.get('filter.v.price.gte') === "0.00") {
      searchParams.delete('filter.v.price.gte');
    }
    if (document.querySelector('.price_slider')) {
      if (searchParams.get('filter.v.price.lte') === parseFloat(document.querySelector('.price_slider').dataset.max).toFixed(2)) {
        searchParams.delete('filter.v.price.lte');
      }
    }
    // Pass target index and form ID for matching index preservation
    FacetFiltersForm.renderPage(searchParams.toString(), { targetIndex, targetFormId });
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }

  onMore(e) {
    let ul = this.closest('.thb-filter-content').querySelector('ul');

    if (ul.hasAttribute('filters-open')) {
      this.textContent = theme.strings.showMore;
      ul.removeAttribute('filters-open');
    } else {
      this.textContent = theme.strings.showLess;
      ul.setAttribute('filters-open', '');
    }
    e.preventDefault();
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/**
 *  @class
 *  @function FacetRemove
 */
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('a').forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        
        // Find the correct form - check if inside mobile drawer first
        let form = this.closest('facet-filters-form');
        
        if (!form) {
          // If inside mobile drawer (FacetFiltersFormMobile wrapper), find the form inside it
          const mobileWrapper = this.closest('FacetFiltersFormMobile') || this.closest('.facet-drawer');
          if (mobileWrapper) {
            form = mobileWrapper.querySelector('facet-filters-form');
          }
        }
        
        // Fallback to any form on the page
        if (!form) {
          form = document.querySelector('facet-filters-form');
        }
        
        if (form && form.onActiveFilterClick) {
          form.onActiveFilterClick(event);
        } else {
          // Direct navigation fallback if form not found
          const url = event.currentTarget.href;
          FacetFiltersForm.renderPage(url.indexOf('?') == -1 ? '' : url.slice(url.indexOf('?') + 1));
        }
      });
    });
  }
}

customElements.define('facet-remove', FacetRemove);


/**
 *  @class
 *  @function FacetToolbar
 */
class FacetToolbar extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.productGrid = this.closest('#ProductGridContainer');
    this.compareToggle = this.querySelector('#compare_toggle');
    this.layoutSwitcher = this.querySelector('.facets--bar-view-as');
    this.buttons = this.querySelectorAll('.facets--bar-view-as-button');
    this.productLayout = window.localStorage.getItem('product-layout');
    if (this.layoutSwitcher) {
      this.buttons.forEach((button) => {
        button.addEventListener('click', (evt) => {
          [].forEach.call(this.buttons, function (el) {
            el.removeAttribute('disabled');
          });
          this.onLayoutChange(button, button.dataset.value);
          evt.preventDefault();
        });
      });

      if (this.productLayout && this.productLayout === 'list') {
        const toggle = this.querySelector(`.facets--bar-view-as-button-${this.productLayout}`);
        if (toggle) toggle.click();
      }
    }
    if (this.compareToggle) {
      this.compareToggle.addEventListener('change', this.onCompare);
    }
  }
  onLayoutChange(button, value) {
    button.setAttribute('disabled', '');
    this.productGrid.dataset.layout = value;
    window.localStorage.setItem('product-layout', value);
  }
  onCompare(e) {
    document.body.classList.toggle('compare-true');
    e.preventDefault();
  }
}
customElements.define('facet-toolbar', FacetToolbar);

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {

  constructor() {
    super();
    this.isDragging = false;
    this.initialized = false;
    console.log('[PriceSlider] Constructor called');
  }
  connectedCallback() {
    console.log('[PriceSlider] connectedCallback started');
    this.slider = this.querySelector('.price_slider');
    console.log('[PriceSlider] slider element:', this.slider);
    
    // Support both desktop (.price_slider_amount) and mobile (.facet-price-inputs) class names
    this.amounts = this.querySelector('.price_slider_amount') || this.querySelector('.facet-price-inputs');
    console.log('[PriceSlider] amounts element:', this.amounts);
    
    this.form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    console.log('[PriceSlider] form element:', this.form);
    
    this.inputEvent = new CustomEvent('input');

    // Use IntersectionObserver to initialize slider only when visible
    // This fixes the issue where slider initializes off-screen (in hidden drawer)
    this.setupVisibilityObserver();
    
    // Also try initializing if already visible (e.g. desktop sidebar)
    this.checkAndInitSlider();
    
    // Prevent dropdown from closing when interacting with slider on mobile
    this.preventDropdownClose();
  }
  
  setupVisibilityObserver() {
    // Watch for when the slider becomes visible
    this.visibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        console.log('[PriceSlider] Visibility changed:', entry.isIntersecting, 'ratio:', entry.intersectionRatio);
        if (entry.isIntersecting && entry.intersectionRatio > 0) {
          this.checkAndInitSlider();
        }
      });
    }, { threshold: [0, 0.1, 0.5, 1] });
    
    this.visibilityObserver.observe(this.slider || this);
    
    // CRITICAL: Watch for <details> element opening (slider is inside a collapsible section)
    this.detailsElement = this.closest('details');
    if (this.detailsElement) {
      console.log('[PriceSlider] Found <details> parent, setting up toggle listener');
      this.detailsElement.addEventListener('toggle', () => {
        console.log('[PriceSlider] Details toggled, open:', this.detailsElement.open);
        if (this.detailsElement.open) {
          // Delay to allow the content to render with proper dimensions
          setTimeout(() => {
            this.reinitializeSlider();
          }, 100);
        }
      });
    }
    
    // Also listen for drawer open events (class changes)
    this.drawer = this.closest('.facet-drawer') || this.closest('.side-panel');
    if (this.drawer) {
      console.log('[PriceSlider] Found drawer, setting up mutation observer');
      this.drawerObserver = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          if (mutation.attributeName === 'class') {
            const isActive = this.drawer.classList.contains('active');
            console.log('[PriceSlider] Drawer class changed, active:', isActive);
            if (isActive) {
              // Small delay to let CSS transition start
              setTimeout(() => this.checkAndInitSlider(), 50);
              // And check again after transition completes
              setTimeout(() => this.refreshSliderPositions(), 350);
            }
          }
        });
      });
      this.drawerObserver.observe(this.drawer, { attributes: true });
    }
  }
  
  reinitializeSlider() {
    console.log('[PriceSlider] reinitializeSlider called');
    
    // Check if slider is visible now
    const rect = this.slider.getBoundingClientRect();
    console.log('[PriceSlider] Slider rect at reinit:', rect);
    
    if (rect.width === 0 || rect.x < 0) {
      console.log('[PriceSlider] Slider still not properly visible, skipping reinit');
      return;
    }
    
    // Destroy existing slider if any
    if (this.slider.noUiSlider) {
      console.log('[PriceSlider] Destroying existing noUiSlider');
      this.slider.noUiSlider.destroy();
    }
    
    // Reset initialization flag
    this.initialized = false;
    
    // Reinitialize
    this.initSlider();
  }
  
  checkAndInitSlider() {
    // Check if slider is actually visible (positive coordinates, non-zero size)
    if (!this.slider) return;
    
    const rect = this.slider.getBoundingClientRect();
    const isVisible = rect.width > 0 && rect.height > 0 && rect.x >= 0 && rect.right <= window.innerWidth + 50;
    
    console.log('[PriceSlider] checkAndInitSlider - rect:', rect, 'isVisible:', isVisible);
    
    if (isVisible) {
      this.initSlider();
      // Refresh positions after initialization
      setTimeout(() => this.refreshSliderPositions(), 100);
    }
  }
  
  refreshSliderPositions() {
    // Force noUiSlider to recalculate positions when drawer opens
    if (this.slider && this.slider.noUiSlider) {
      const rect = this.slider.getBoundingClientRect();
      console.log('[PriceSlider] refreshSliderPositions - current rect:', rect);
      
      if (rect.x >= 0) {
        // Trigger a recalculation by getting and setting current values
        const currentValues = this.slider.noUiSlider.get();
        console.log('[PriceSlider] Refreshing slider with values:', currentValues);
        
        // This triggers noUiSlider to recalculate internal positions
        this.slider.noUiSlider.updateOptions({
          range: {
            'min': 0,
            'max': parseFloat(this.slider.dataset.max)
          }
        }, false);
      }
    }
  }
  
  initSlider() {
    console.log('[PriceSlider] initSlider called, initialized:', this.initialized);
    console.log('[PriceSlider] noUiSlider available:', typeof noUiSlider !== 'undefined');
    
    // Wait for noUiSlider to be available
    if (typeof noUiSlider === 'undefined') {
      console.log('[PriceSlider] noUiSlider not loaded yet, retrying in 100ms...');
      setTimeout(() => this.initSlider(), 100);
      return;
    }
    
    // Don't reinitialize if already created
    if (this.slider.noUiSlider || this.initialized) {
      console.log('[PriceSlider] Slider already initialized, skipping');
      return;
    }
    
    // Check if slider element exists
    if (!this.slider) {
      console.error('[PriceSlider] ERROR: slider element (.price_slider) not found!');
      return;
    }
    
    // CRITICAL: Check if element is visible before initializing
    const rect = this.slider.getBoundingClientRect();
    if (rect.x < 0 || rect.width === 0) {
      console.log('[PriceSlider] Slider not visible yet (x:', rect.x, 'width:', rect.width, '), deferring init');
      return;
    }
    
    console.log('[PriceSlider] Slider dataset:', {
      minValue: this.slider.dataset.minValue,
      maxValue: this.slider.dataset.maxValue,
      max: this.slider.dataset.max
    });

    const args = {
      start: [
        parseFloat(this.slider.dataset.minValue || 0),
        parseFloat(this.slider.dataset.maxValue || this.slider.dataset.max)
      ],
      connect: true,
      step: 10,
      direction: document.dir,
      handleAttributes: [
        { 'aria-label': 'lower' },
        { 'aria-label': 'upper' },
      ],
      range: {
        'min': 0,
        'max': parseFloat(this.slider.dataset.max)
      }
    };
    console.log('[PriceSlider] Creating noUiSlider with args:', args);
    console.log('[PriceSlider] Slider rect at init time:', rect);
    
    try {
      noUiSlider.create(this.slider, args);
      this.initialized = true;
      console.log('[PriceSlider] noUiSlider created successfully');
    } catch (error) {
      console.error('[PriceSlider] ERROR creating noUiSlider:', error);
      return;
    }

    this.slider.noUiSlider.on('update', this.onSliderUpdate.bind(this));
    this.slider.noUiSlider.on('change', this.onSliderChange.bind(this));
    
    // Track drag state to prevent dropdown closing
    this.slider.noUiSlider.on('start', () => {
      console.log('[PriceSlider] Drag started');
      this.isDragging = true;
      document.body.classList.add('price-slider-dragging');
    });
    this.slider.noUiSlider.on('end', () => {
      console.log('[PriceSlider] Drag ended');
      this.isDragging = false;
      document.body.classList.remove('price-slider-dragging');
    });
    
    // Debug: Check computed styles
    const sliderStyles = window.getComputedStyle(this.slider);
    console.log('[PriceSlider] Slider computed styles:', {
      pointerEvents: sliderStyles.pointerEvents,
      touchAction: sliderStyles.touchAction,
      userSelect: sliderStyles.userSelect,
      display: sliderStyles.display,
      position: sliderStyles.position,
      width: sliderStyles.width,
      height: sliderStyles.height
    });
    
    // Check for handles
    const handles = this.slider.querySelectorAll('.noUi-handle');
    console.log('[PriceSlider] Number of handles found:', handles.length);
    handles.forEach((handle, i) => {
      const handleStyles = window.getComputedStyle(handle);
      console.log(`[PriceSlider] Handle ${i} styles:`, {
        pointerEvents: handleStyles.pointerEvents,
        touchAction: handleStyles.touchAction,
        display: handleStyles.display,
        position: handleStyles.position,
        width: handleStyles.width,
        height: handleStyles.height
      });
    });
  }
  
  preventDropdownClose() {
    // REMOVED: All stopPropagation and event listeners that were potentially
    // interfering with noUiSlider's event handling.
    // noUiSlider needs full control of mouse/touch events on its elements.
    console.log('[PriceSlider] preventDropdownClose - keeping it minimal to not interfere with noUiSlider');
  }
  
  // Debug method - call from console: document.querySelector('price-slider').debugSlider()
  debugSlider() {
    console.log('=== PRICE SLIDER DEBUG ===');
    console.log('Slider element:', this.slider);
    console.log('Has noUiSlider:', !!this.slider?.noUiSlider);
    console.log('Initialized flag:', this.initialized);
    
    if (this.slider) {
      const rect = this.slider.getBoundingClientRect();
      console.log('Slider rect:', rect);
      console.log('Slider visible:', rect.width > 0 && rect.x >= 0);
      
      const styles = window.getComputedStyle(this.slider);
      console.log('Slider styles:', {
        pointerEvents: styles.pointerEvents,
        touchAction: styles.touchAction,
        display: styles.display
      });
      
      const handles = this.slider.querySelectorAll('.noUi-handle');
      console.log('Handles found:', handles.length);
      handles.forEach((h, i) => {
        const hr = h.getBoundingClientRect();
        const hs = window.getComputedStyle(h);
        console.log(`Handle ${i}:`, { rect: hr, pointerEvents: hs.pointerEvents, touchAction: hs.touchAction });
      });
    }
    
    // Check if there's a conflicting noUiSlider
    const allSliders = document.querySelectorAll('.noUi-target');
    console.log('All noUiSlider instances on page:', allSliders.length);
  }
  
  onSliderUpdate(values) {
    this.amounts.querySelector('.field__input_min').value = values[0];
    this.amounts.querySelector('.field__input_max').value = values[1];
  }

  onSliderChange(values) {
    this.form.querySelector('form').dispatchEvent(this.inputEvent);
  }
}
customElements.define('price-slider', PriceSlider);